import { QueryClient } from "@tanstack/react-query";
import { PostHog } from "posthog-js";
import * as React from "react";
import { Outlet, redirect, RouteObject } from "react-router";
import { assignmentSubmissionViewLoader } from "~/participant/views/AssignmentSubmissionView.loader";
import { assignmentViewLoader } from "~/participant/views/AssignmentView/AssignmentView.loader";
import { USER_ACCESS } from "~/shared/api/users/users.constants";
import { getRouteTreeRoot } from "~/shared/api/users/users.helpers";
import { USERS_QUERIES } from "~/shared/api/users/users.queries";
import { TeamAssignmentNotFound } from "~/shared/components/AssignmentNotFound";
import { AssignmentSubmissionNotFound } from "~/shared/components/AssignmentSubmissionNotFound";
import { TeamContentLibraryNotFound } from "~/shared/components/ContentLibraryNotFound";
import { ButtonLink } from "~/shared/components/ds/ButtonLink";
import { LoadingContainer } from "~/shared/components/ds/Spinner";
import {
  EntityErrorHandler,
  NestedRouteErrorHandler,
  TopLevelRouteErrorHandler,
} from "~/shared/components/errors/Error";
import { NotFound } from "~/shared/components/errors/NotFound";
import { TeamEventNotFound } from "~/shared/components/EventNotFound";
import { TeamInterviewNotFound } from "~/shared/components/InterviewNotFound";
import { JobNotFound } from "~/shared/components/JobNotFound";
import { PrivateAppLayout } from "~/shared/components/PrivateAppLayout/PrivateAppLayout";
import { TeamSurveyNotFound } from "~/shared/components/SurveyNotFound";
import { SurveySubmissionNotFound } from "~/shared/components/SurveySubmissionNotFound";
import { userAccessGuardLoader } from "~/shared/guards/userAccess.guard";
import { lazyRetry } from "~/shared/util/lazyRetry";
import { accountPreferencesViewLoader } from "~/shared/views/AccountPreferences/AccountPreferencesView.loader";
import {
  ASSIGNMENT_PATHS,
  AUTOMATION_PATHS,
  EVENTS_PATHS,
  JOB_PATHS,
  SURVEYS_PATHS,
  TEAM_EXPERIENCE_PREFIX,
} from "~/team/constants/paths.constants";
import { accountTwoFactorAuthViewLoader } from "~/team/views/AccountTwoFactorAuthView.loader";
import { assignmentPreviewViewLoader } from "~/team/views/AssignmentPreviewView/AssignmentPreviewView.loader";
import { editAssignmentViewLoader } from "~/team/views/EditAssignmentView.loader";
import { editInterviewViewLoader } from "~/team/views/EditInterviewView/EditInterviewView.loader";
import { editSurveyViewLoader } from "~/team/views/EditSurveyView/EditSurveyView.loader";
import { eventCreateViewLoader } from "~/team/views/EventCreateView.loader";
import { eventEditViewLoader } from "~/team/views/EventEditView.loader";
import { eventViewLoader } from "~/team/views/EventView/EventView.loader";
import { jobInterviewsViewLoader } from "~/team/views/JobInterviewsView/JobInterviewsView.loader";
import { jobParticipantGamePlansViewLoader } from "~/team/views/JobParticipantGamePlansView/JobParticipantGamePlansView.loader";
import { participantSavedRolesViewLoader } from "~/team/views/ParticipantSavedRolesView.loader";
import { participantViewLoader } from "~/team/views/ParticipantView.loader";
import { partnerLoader } from "~/team/views/PartnerView/PartnerView.loader";
import { pipelineViewLoader } from "~/team/views/PipelineView.loader";
import { previewSurveyViewLoader } from "~/team/views/PreviewSurveyView/PreviewSurveyView.loader";
import { surveyDetailViewLoader } from "~/team/views/SurveyDetailView.loader";
import { surveysViewLoader } from "~/team/views/SurveysView.loader";
import { editPartnerContactViewLoader } from "./views/EditPartnerContactView/EditPartnerContactView.loader";
import { jobEditStatusesViewLoader } from "./views/JobEditStatusesView/JobEditStatusesView.loader";
import { jobGamePlansViewLoader } from "./views/JobGamePlansView/JobGamePlansView.loader";
import { jobViewLoader } from "./views/JobView/JobView.loader";
import { ParticipantNotFound } from "./views/ParticipantsView/components/ParticipantNotFound";
import { PartnerContactNotFound } from "./views/PartnerContactView/components/PartnerContactNotFound";
import { partnerEditLoader } from "./views/PartnerEditView/PartnerEditView.loader";
import { partnersViewLoader } from "./views/PartnersView/PartnersView.loader";
import { PartnerNotFound } from "./views/PartnerView/components/PartnerNotFound";

export function getTeamRoutes(queryClient: QueryClient, postHogClient?: PostHog): Array<RouteObject> {
  return [
    {
      path: TEAM_EXPERIENCE_PREFIX,
      loader: async (args) => {
        const user = await queryClient.ensureQueryData(USERS_QUERIES.profile());

        if (user.type === "breakline") return null;

        const routeTreeRoot = getRouteTreeRoot(user.type);

        const { request } = args;
        const url = new URL(request.url);
        const pathname = url.pathname;

        /**
         * Because the user isn't a BreakLine teammember, we want to remove `/hq` prefix and redirect them to the
         * modified pathname without it.
         *
         * Example: `/hq/jobs` becomes `/jobs` for participants
         */
        const modifiedRoute = pathname.replace(new RegExp(`^/${TEAM_EXPERIENCE_PREFIX}/?`), routeTreeRoot);

        return redirect(modifiedRoute);
      },
      errorElement: <TopLevelRouteErrorHandler />,
      element: (
        <PrivateAppLayout>
          <React.Suspense fallback={<LoadingContainer level="page" />}>
            <Outlet />
          </React.Suspense>
        </PrivateAppLayout>
      ),
      children: [
        {
          index: true,
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { HomeView } = await lazyRetry(() => import("~/team/views/HomeView/HomeView"), "HomeView");
            return {
              Component: HomeView,
            };
          },
        },
        {
          path: "notifications",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { NotificationsView } = await lazyRetry(
              () => import("~/team/views/NotificationsView"),
              "NotificationsView",
            );
            return {
              Component: NotificationsView,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { NotificationsListView } = await lazyRetry(
                  () => import("~/shared/views/NotificationsListView/NotificationsListView"),
                  "NotificationsListView",
                );
                return {
                  Component: NotificationsListView,
                };
              },
            },
            {
              path: "tasks",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { NotificationsTasksView } = await lazyRetry(
                  () => import("~/team/views/NotificationTasksView/NotificationsTasksView"),
                  "NotificationsTasksView",
                );
                return {
                  Component: NotificationsTasksView,
                };
              },
            },
            {
              path: "outgoing",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { NotificationsOutgoingView } = await lazyRetry(
                  () => import("~/team/views/NotificationsOutgoingView"),
                  "NotificationsOutgoingView",
                );
                return {
                  Component: NotificationsOutgoingView,
                };
              },
            },
            {
              path: "manager",
              errorElement: <NestedRouteErrorHandler />,
              loader: () => {
                return userAccessGuardLoader([USER_ACCESS.admin], queryClient);
              },
              lazy: async () => {
                const { NotificationsManagerView } = await lazyRetry(
                  () => import("~/team/views/NotificationsManagerView"),
                  "NotificationsManagerView",
                );
                return {
                  Component: NotificationsManagerView,
                };
              },
            },
          ],
        },
        {
          path: "jobs",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { JobsView } = await lazyRetry(() => import("~/team/views/JobsView"), "JobsView");
            return {
              Component: JobsView,
            };
          },
        },
        {
          path: "jobs/create",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { CreateJobView } = await lazyRetry(
              () => import("~/team/views/CreateJobView/CreateJobView"),
              "CreateJobView",
            );
            return {
              Component: CreateJobView,
            };
          },
        },
        {
          path: "jobs/:jobId",
          errorElement: (
            <EntityErrorHandler
              renderNotFound={() => (
                <JobNotFound>
                  <ButtonLink variant="default" to={JOB_PATHS.root}>
                    View Roles
                  </ButtonLink>
                </JobNotFound>
              )}
            />
          ),
          loader: async (args) => {
            return jobViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { JobView } = await lazyRetry(() => import("~/team/views/JobView/JobView"), "JobView");
            return {
              Component: JobView,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              loader: (args) => {
                return jobParticipantGamePlansViewLoader(queryClient, args);
              },
              shouldRevalidate: () => false,
              lazy: async () => {
                const { JobParticipantGamePlansView } = await lazyRetry(
                  () => import("~/team/views/JobParticipantGamePlansView/JobParticipantGamePlansView"),
                  "JobParticipantGamePlansView",
                );
                return {
                  element: <JobParticipantGamePlansView />,
                };
              },
            },
            {
              path: "interviews",
              errorElement: <NestedRouteErrorHandler />,
              loader: (args) => {
                return jobInterviewsViewLoader(queryClient, args);
              },
              shouldRevalidate: () => false,
              lazy: async () => {
                const { JobInterviewsView } = await lazyRetry(
                  () => import("~/team/views/JobInterviewsView/JobInterviewsView"),
                  "JobInterviewsView",
                );
                return {
                  element: <JobInterviewsView />,
                };
              },
            },
            {
              path: "edit",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { EditJobView } = await lazyRetry(
                  () => import("~/team/views/EditJobView/EditJobView"),
                  "EditJobView",
                );
                return {
                  element: (
                    <React.Suspense fallback={<LoadingContainer level="component" />}>
                      <EditJobView />
                    </React.Suspense>
                  ),
                };
              },
            },
            {
              path: "insight",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { JobInsightView } = await lazyRetry(
                  () => import("~/team/views/JobInsightView/JobInsightView"),
                  "JobInsightView",
                );
                return {
                  element: (
                    <React.Suspense fallback={<LoadingContainer level="component" />}>
                      <JobInsightView />
                    </React.Suspense>
                  ),
                };
              },
            },
            {
              path: "edit/statuses",
              errorElement: <NestedRouteErrorHandler />,
              loader: (args) => {
                return jobEditStatusesViewLoader(queryClient, args);
              },
              shouldRevalidate: () => false,
              lazy: async () => {
                const { JobEditStatusesView } = await lazyRetry(
                  () => import("~/team/views/JobEditStatusesView/JobEditStatusesView"),
                  "JobEditStatusesView",
                );
                return {
                  element: <JobEditStatusesView />,
                };
              },
            },

            {
              path: "role-groups",
              shouldRevalidate: () => false,
              lazy: async () => {
                const { JobRoleGroupsView } = await lazyRetry(
                  () => import("~/team/views/JobRoleGroupsView/JobRoleGroupsView"),
                  "JobRoleGroupsView",
                );
                return {
                  element: <JobRoleGroupsView />,
                };
              },
            },
          ],
        },
        {
          path: "role-groups",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { RoleGroupsView } = await lazyRetry(() => {
              return import("~/team/views/RoleGroupsView/RoleGroupsView");
            }, "RoleGroupsView");

            return {
              element: <RoleGroupsView />,
            };
          },
        },
        {
          path: "account",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { AccountView } = await lazyRetry(() => import("~/team/views/AccountView"), "AccountView");
            return {
              element: <AccountView />,
            };
          },
          children: [
            {
              index: true,
              loader: () => {
                return accountPreferencesViewLoader(queryClient);
              },
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { AccountPreferencesView } = await lazyRetry(
                  () => import("~/shared/views/AccountPreferences/AccountPreferencesView"),
                  "AccountPreferencesView",
                );
                return {
                  element: <AccountPreferencesView />,
                };
              },
            },
            {
              path: "notifications",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { AccountNotificationsPreferencesView } = await lazyRetry(
                  () => import("~/shared/views/AccountNotificationsPreferencesView"),
                  "AccountNotificationsPreferencesView",
                );
                return {
                  element: <AccountNotificationsPreferencesView />,
                };
              },
            },
          ],
        },
        {
          path: "account/two-factor-authentication/new",
          errorElement: <TopLevelRouteErrorHandler />,
          loader: () => {
            return accountTwoFactorAuthViewLoader(queryClient);
          },
          lazy: async () => {
            const { AccountTwoFactorAuthView } = await lazyRetry(
              () => import("~/team/views/AccountTwoFactorAuthView"),
              "AccountTwoFactorAuthView",
            );
            return {
              element: <AccountTwoFactorAuthView />,
            };
          },
        },
        {
          path: "interviews/:interviewId",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamInterviewNotFound />} />,
          lazy: async () => {
            const { InterviewView } = await lazyRetry(() => import("~/team/views/InterviewView"), "InterviewView");
            return {
              Component: InterviewView,
            };
          },
        },
        {
          path: "interviews/:interviewId/edit",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamInterviewNotFound />} />,
          loader: (args) => {
            return editInterviewViewLoader(queryClient, args);
          },
          lazy: async () => {
            const { EditInterviewView } = await lazyRetry(
              () => import("~/team/views/EditInterviewView/EditInterviewView"),
              "EditInterview",
            );
            return {
              Component: EditInterviewView,
            };
          },
        },
        {
          path: "interviews/create",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { CreateInterviewView } = await lazyRetry(
              () => import("~/team/views/CreateInterviewView"),
              "CreateInterviewView",
            );
            return {
              Component: CreateInterviewView,
            };
          },
        },
        // PLAYGROUND
        {
          path: "playground",
          errorElement: <TopLevelRouteErrorHandler />,
          loader: () => {
            return userAccessGuardLoader([USER_ACCESS.admin], queryClient);
          },
          lazy: async () => {
            const { PlaygroundView } = await lazyRetry(() => import("~/team/views/PlaygroundView"), "PlaygroundView");

            return {
              Component: PlaygroundView,
            };
          },
        },
        // PIPELINE
        {
          path: "pipeline-plus",
          loader: (args) => {
            return pipelineViewLoader(queryClient, args);
          },
          shouldRevalidate: () => false,
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { PipelineView } = await lazyRetry(() => import("~/team/views/PipelineView"), "PipelineView");

            return {
              Component: PipelineView,
            };
          },
          children: [
            {
              index: true,
              shouldRevalidate: () => false,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { RolesView } = await lazyRetry(() => import("~/team/views/RolesView"), "RolesView");

                return {
                  Component: RolesView,
                };
              },
            },
            {
              path: "interviews",
              shouldRevalidate: () => false,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { InterviewsView } = await lazyRetry(
                  () => import("~/team/views/InterviewsView/InterviewsView"),
                  "InterviewsView",
                );

                return {
                  Component: InterviewsView,
                };
              },
            },
          ],
        },
        // EVENTS
        {
          path: "events",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { EventsView } = await lazyRetry(() => import("~/team/views/EventsView"), "EventsView");
            return {
              Component: EventsView,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { EventTypeView } = await lazyRetry(
                  () => import("~/shared/views/EventTypeView"),
                  "EventTypeView",
                );
                return {
                  Component: EventTypeView,
                };
              },
            },
            {
              path: "type/:eventTypeId",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { EventTypeView } = await lazyRetry(
                  () => import("~/shared/views/EventTypeView"),
                  "EventTypeView",
                );
                return {
                  Component: EventTypeView,
                };
              },
            },
            {
              path: "type/all",
              loader: () => {
                return redirect(EVENTS_PATHS.root);
              },
            },
          ],
        },
        {
          path: "events/:eventId",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamEventNotFound />} />,
          shouldRevalidate: () => false,
          loader: async (args) => {
            return eventViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { EventView } = await lazyRetry(() => import("~/team/views/EventView/EventView"), "EventView");
            return {
              Component: EventView,
            };
          },
        },
        {
          path: "events/create",
          errorElement: <TopLevelRouteErrorHandler />,
          shouldRevalidate: () => false,
          loader: (args) => {
            return eventCreateViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { EventCreateView } = await lazyRetry(
              () => import("~/team/views/EventCreateView"),
              "EventCreateView",
            );
            return {
              Component: EventCreateView,
            };
          },
        },
        {
          path: "events/:eventId/edit",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamEventNotFound />} />,
          shouldRevalidate: () => false,
          loader: (args) => {
            return eventEditViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { EventEditView } = await lazyRetry(() => import("~/team/views/EventEditView"), "EditEventView");
            return {
              Component: EventEditView,
            };
          },
        },
        // ASSIGNMENTS
        {
          path: "assignments",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { AssignmentsView } = await lazyRetry(
              () => import("~/team/views/AssignmentsView/AssignmentsView"),
              "AssignmentsView",
            );
            return {
              Component: AssignmentsView,
            };
          },
        },
        {
          path: "assignments/create",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { CreateAssignmentView } = await lazyRetry(
              () => import("~/team/views/CreateAssignmentView"),
              "CreateAssignmentView",
            );
            return {
              Component: CreateAssignmentView,
            };
          },
        },
        {
          path: "assignments/submissions",
          loader: () => redirect(ASSIGNMENT_PATHS.root),
        },
        {
          path: "assignments/:assignmentId/edit",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamAssignmentNotFound />} />,
          shouldRevalidate: () => false,
          loader: async (args) => {
            return editAssignmentViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { EditAssignmentView } = await lazyRetry(
              () => import("~/team/views/EditAssignmentView"),
              "EditAssignmentView",
            );
            return {
              Component: EditAssignmentView,
            };
          },
        },
        {
          path: "assignments/:assignmentId/preview",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamAssignmentNotFound />} />,
          shouldRevalidate: () => false,
          loader: async (args) => {
            return assignmentPreviewViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { AssignmentPreviewView } = await lazyRetry(
              () => import("~/team/views/AssignmentPreviewView/AssignmentPreviewView"),
              "AssignmentPreviewView",
            );
            return {
              Component: AssignmentPreviewView,
            };
          },
        },
        {
          path: "assignments/:assignmentId",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamAssignmentNotFound />} />,
          shouldRevalidate: () => false,
          loader: async (args) => {
            return assignmentViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { AssignmentView } = await lazyRetry(
              () => import("~/team/views/AssignmentView/AssignmentView"),
              "AssignmentView",
            );
            return {
              Component: AssignmentView,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { SubmissionsListView } = await lazyRetry(
                  () => import("~/team/views/SubmissionsListView"),
                  "SubmissionsListView",
                );
                return {
                  element: <SubmissionsListView type="assigned" />,
                };
              },
            },
            {
              path: "submissions",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { SubmissionsListView } = await lazyRetry(
                  () => import("~/team/views/SubmissionsListView"),
                  "SubmissionsListView",
                );
                return {
                  element: <SubmissionsListView type="submissions" />,
                };
              },
            },
          ],
        },
        {
          path: "assignments/submissions/:submissionId",
          errorElement: (
            <EntityErrorHandler
              renderNotFound={() => (
                <AssignmentSubmissionNotFound>
                  <ButtonLink to={ASSIGNMENT_PATHS.root}>View Assignments</ButtonLink>
                </AssignmentSubmissionNotFound>
              )}
            />
          ),
          shouldRevalidate: () => false,
          loader: async (args) => {
            return assignmentSubmissionViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { AssignmentSubmissionView } = await lazyRetry(
              () => import("~/team/views/AssignmentSubmissionView"),
              "AssignmentSubmissionView",
            );
            return {
              Component: AssignmentSubmissionView,
            };
          },
        },
        // SURVEYS
        {
          path: "surveys",
          errorElement: <TopLevelRouteErrorHandler />,
          shouldRevalidate: () => false,
          loader: () => {
            return surveysViewLoader(queryClient);
          },
          lazy: async () => {
            const { SurveysView } = await lazyRetry(() => import("~/team/views/SurveysView"), "SurveysView");
            return {
              Component: SurveysView,
            };
          },
        },
        {
          path: "surveys/create",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { CreateSurveyView } = await lazyRetry(
              () => import("~/team/views/CreateSurveyView"),
              "CreateSurveyView",
            );
            return {
              Component: CreateSurveyView,
            };
          },
        },
        {
          path: "surveys/:surveyId/edit",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamSurveyNotFound />} />,
          shouldRevalidate: () => false,
          loader: async (args) => {
            return editSurveyViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { EditSurveyView } = await lazyRetry(
              () => import("~/team/views/EditSurveyView/EditSurveyView"),
              "EditSurveyView",
            );
            return {
              Component: EditSurveyView,
            };
          },
        },
        {
          path: "surveys/:surveyId/preview",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamSurveyNotFound />} />,
          shouldRevalidate: () => false,
          loader: async (args) => {
            return previewSurveyViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { PreviewSurveyView } = await lazyRetry(
              () => import("~/team/views/PreviewSurveyView/PreviewSurveyView"),
              "PreviewSurveyView",
            );
            return {
              Component: PreviewSurveyView,
            };
          },
        },
        {
          path: "surveys/submissions",
          loader: () => {
            return redirect(SURVEYS_PATHS.root);
          },
        },
        {
          path: "surveys/:surveyId",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamSurveyNotFound />} />,
          shouldRevalidate: () => false,
          loader: async (args) => {
            return surveyDetailViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { SurveyDetailView } = await lazyRetry(
              () => import("~/team/views/SurveyDetailView"),
              "SurveyDetailView",
            );
            return {
              element: <SurveyDetailView />,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { SurveyListView } = await lazyRetry(
                  () => import("~/team/views/SurveyListView"),
                  "SurveyListView",
                );
                return {
                  element: <SurveyListView type="assigned" />,
                };
              },
            },
            {
              path: "submissions",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { SurveyListView } = await lazyRetry(
                  () => import("~/team/views/SurveyListView"),
                  "SurveyListView",
                );
                return {
                  element: <SurveyListView type="submissions" />,
                };
              },
            },
          ],
        },
        {
          path: "surveys/submissions/:submissionId",
          errorElement: (
            <EntityErrorHandler
              renderNotFound={() => (
                <SurveySubmissionNotFound>
                  <ButtonLink to={SURVEYS_PATHS.root}>View all surveys</ButtonLink>
                </SurveySubmissionNotFound>
              )}
            />
          ),
          lazy: async () => {
            const { SubmissionView } = await lazyRetry(
              () => import("~/team/views/SurveySubmissionView"),
              "SubmissionView",
            );
            return {
              Component: SubmissionView,
            };
          },
        },
        {
          path: "participants",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { ParticipantsView } = await lazyRetry(
              () => import("~/team/views/ParticipantsView/ParticipantsView"),
              "ParticipantsView",
            );
            return {
              Component: ParticipantsView,
            };
          },
        },
        {
          path: "participants/create",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { ParticipantCreateView } = await lazyRetry(
              () => import("~/team/views/ParticipantCreateView/ParticipantCreateView"),
              "ParticipantCreateView",
            );
            return {
              Component: ParticipantCreateView,
            };
          },
        },
        {
          path: "participants/:participantId/edit",
          errorElement: <EntityErrorHandler renderNotFound={() => <ParticipantNotFound />} />,
          lazy: async () => {
            const { EditParticipantView } = await lazyRetry(
              () => import("~/team/views/EditParticipantView/EditParticipantView"),
              "EditParticipantView",
            );
            return {
              Component: EditParticipantView,
            };
          },
        },
        {
          path: "participants/:participantId",
          errorElement: <EntityErrorHandler renderNotFound={() => <ParticipantNotFound />} />,
          shouldRevalidate: () => false,
          loader: (args) => {
            return participantViewLoader(queryClient, args);
          },
          lazy: async () => {
            const { ParticipantView } = await lazyRetry(() => import("~/team/views/ParticipantView"), "Participant");
            return {
              Component: ParticipantView,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { ParticipantProfileView } = await lazyRetry(
                  () => import("~/team/views/ParticipantProfileView/ParticipantProfileView"),
                  "ParticipantProfileView",
                );
                return {
                  Component: ParticipantProfileView,
                };
              },
            },
            {
              path: "opportunities",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { ParticipantOpportunitiesView } = await lazyRetry(
                  () => import("~/team/views/ParticipantOpportunitiesView/ParticipantOpportunitiesView"),
                  "ParticipantOpportunitiesView",
                );
                return {
                  Component: ParticipantOpportunitiesView,
                };
              },
            },
            {
              path: "coaching",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { ParticipantCoachingView } = await lazyRetry(
                  () => import("~/team/views/ParticipantCoachingView/ParticipantCoachingView"),
                  "ParticipantCoachingView",
                );
                return {
                  Component: ParticipantCoachingView,
                };
              },
            },
            {
              path: "statuses",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { ParticipantStatusesView } = await lazyRetry(
                  () => import("~/team/views/ParticipantStatusesView/ParticipantStatusesView"),
                  "ParticipantStatusesView",
                );
                return {
                  Component: ParticipantStatusesView,
                };
              },
            },
            {
              path: "role-groups",
              lazy: async () => {
                const { ParticipantRoleGroupsView } = await lazyRetry(
                  () => import("~/team/views/ParticipantRoleGroupsView/ParticipantRoleGroupsView"),
                  "ParticipantRoleGroupsView",
                );
                return {
                  element: <ParticipantRoleGroupsView />,
                };
              },
            },
            {
              path: "saved",
              errorElement: <NestedRouteErrorHandler />,
              loader: async (args) => {
                return participantSavedRolesViewLoader(args, queryClient);
              },
              lazy: async () => {
                const { ParticipantSavedRolesView } = await lazyRetry(
                  () => import("~/team/views/ParticipantSavedRolesView"),
                  "ParticipantSavedRolesView",
                );

                return {
                  shouldRevalidate: () => false,
                  Component: ParticipantSavedRolesView,
                };
              },
            },
          ],
        },
        // TAGS
        {
          path: "tags",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { TagsView } = await lazyRetry(() => import("~/team/views/TagsView"), "TagsView");

            return {
              Component: TagsView,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { IndexTagsSkillsView } = await lazyRetry(
                  () => import("~/team/views/TagsSkillsView"),
                  "IndexTagsSkillsView",
                );
                return {
                  Component: IndexTagsSkillsView,
                };
              },
            },
            {
              path: "skills",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsSkillsView } = await lazyRetry(
                  () => import("~/team/views/TagsSkillsView"),
                  "TagsSkillsView",
                );
                return {
                  Component: TagsSkillsView,
                };
              },
            },
            {
              path: "tracks",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsTracksView } = await lazyRetry(
                  () => import("~/team/views/TagsTracksView"),
                  "TagsTracksView",
                );
                return {
                  Component: TagsTracksView,
                };
              },
            },
            {
              path: "locations",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsLocationsView } = await lazyRetry(
                  () => import("~/team/views/TagsLocationsView"),
                  "TagsLocationsView",
                );
                return {
                  Component: TagsLocationsView,
                };
              },
            },
            {
              path: "job-functions",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsJobFunctionsView } = await lazyRetry(
                  () => import("~/team/views/TagsJobFunctionsView"),
                  "TagsJobFunctionsView",
                );
                return {
                  Component: TagsJobFunctionsView,
                };
              },
            },
            {
              path: "content-categories",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsContentCategoriesView } = await lazyRetry(
                  () => import("~/team/views/TagsContentCategoriesView"),
                  "TagsContentCategoriesView",
                );
                return {
                  Component: TagsContentCategoriesView,
                };
              },
            },
            {
              errorElement: <NestedRouteErrorHandler />,
              path: "event-categories",
              lazy: async () => {
                const { TagsEventCategoriesView } = await lazyRetry(
                  () => import("~/team/views/TagsEventCategoriesView"),
                  "TagsEventCategoriesView",
                );
                return {
                  Component: TagsEventCategoriesView,
                };
              },
            },
            {
              path: "self-identifications",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsSelfIdentificationsView } = await lazyRetry(
                  () => import("~/team/views/TagsSelfIdentificationsView"),
                  "TagsSelfIdentificationsView",
                );
                return {
                  Component: TagsSelfIdentificationsView,
                };
              },
            },
            {
              path: "segments",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsSegmentsView } = await lazyRetry(
                  () => import("~/team/views/TagsSegmentsView"),
                  "TagsSegmentsView",
                );
                return {
                  Component: TagsSegmentsView,
                };
              },
            },
            {
              path: "partner-types",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsPartnerTypesView } = await lazyRetry(
                  () => import("~/team/views/TagsPartnerTypesView"),
                  "TagsPartnerTypesView",
                );
                return {
                  Component: TagsPartnerTypesView,
                };
              },
            },
            {
              path: "partner-stages",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsPartnerStagesView } = await lazyRetry(
                  () => import("~/team/views/TagsPartnerStagesView"),
                  "TagsPartnerStagesView",
                );
                return {
                  Component: TagsPartnerStagesView,
                };
              },
            },
            {
              path: "partner-spaces",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsPartnerSpacesView } = await lazyRetry(
                  () => import("~/team/views/TagsPartnerSpacesView"),
                  "TagsPartnerSpacesView",
                );
                return {
                  Component: TagsPartnerSpacesView,
                };
              },
            },
            {
              path: "recruiter-types",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsRecruiterTypesView } = await lazyRetry(
                  () => import("~/team/views/TagsRecruiterTypesView"),
                  "TagsRecruiterTypesView",
                );
                return {
                  Component: TagsRecruiterTypesView,
                };
              },
            },
            {
              path: "recruiter-statuses",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { TagsRecruiterStatusesView } = await lazyRetry(
                  () => import("~/team/views/TagsRecruiterStatusesView"),
                  "TagsRecruiterStatusesView",
                );
                return {
                  Component: TagsRecruiterStatusesView,
                };
              },
            },
          ],
        },
        // TEAM
        {
          path: "team",
          errorElement: <TopLevelRouteErrorHandler />,
          loader: () => {
            return userAccessGuardLoader([USER_ACCESS.admin], queryClient);
          },
          lazy: async () => {
            const { TeamView } = await lazyRetry(() => import("~/team/views/TeamView/TeamView"), "TeamView");

            return {
              Component: TeamView,
            };
          },
        },
        {
          path: "partners",
          errorElement: <TopLevelRouteErrorHandler />,
          loader: async (args) => {
            return partnersViewLoader(queryClient, args);
          },
          lazy: async () => {
            const { PartnersView } = await lazyRetry(
              () => import("~/team/views/PartnersView/PartnersView"),
              "PartnersView",
            );
            return {
              Component: PartnersView,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { FeatureFlaggedPartnersView } = await lazyRetry(
                  () => import("~/team/views/PartnersView/PartnersView"),
                  "FeatureFlaggedPartnersView",
                );
                return {
                  element: <FeatureFlaggedPartnersView />,
                };
              },
            },
            {
              path: "all",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { FeatureFlaggedPartnersView } = await lazyRetry(
                  () => import("~/team/views/PartnersView/PartnersView"),
                  "FeatureFlaggedPartnersView",
                );
                return {
                  element: <FeatureFlaggedPartnersView allPartners />,
                };
              },
            },
          ],
        },
        {
          path: "partners/create",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { CreatePartnerView } = await lazyRetry(
              () => import("~/team/views/CreatePartnerView"),
              "CreatePartnerView",
            );
            return {
              Component: CreatePartnerView,
            };
          },
        },
        {
          path: "partners/:partnerId/edit",
          errorElement: <EntityErrorHandler renderNotFound={() => <PartnerNotFound />} />,
          loader: (args) => {
            return partnerEditLoader(args, queryClient);
          },
          lazy: async () => {
            const { PartnerEditView } = await lazyRetry(
              () => import("~/team/views/PartnerEditView/PartnerEditView"),
              "PartnerEditView",
            );
            return {
              Component: PartnerEditView,
            };
          },
        },
        {
          path: "partners/:partnerId",
          errorElement: <EntityErrorHandler renderNotFound={() => <PartnerNotFound />} />,
          loader: (args) => {
            return partnerLoader(args, queryClient);
          },
          lazy: async () => {
            const { PartnerView } = await lazyRetry(
              () => import("~/team/views/PartnerView/PartnerView"),
              "PartnerView",
            );
            return {
              Component: PartnerView,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { PartnerRolesListView } = await lazyRetry(
                  () => import("~/team/views/PartnerRolesListView"),
                  "PartnerRolesListView",
                );
                return {
                  Component: PartnerRolesListView,
                };
              },
            },
            {
              path: "contact-list",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { PartnerContactsListView } = await lazyRetry(
                  () => import("~/team/views/PartnerContactsListView"),
                  "PartnerContactsListView",
                );
                return {
                  Component: PartnerContactsListView,
                };
              },
            },
            {
              path: "hired",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { PartnerHiredListView } = await lazyRetry(
                  () => import("~/team/views/PartnerHiredListView"),
                  "PartnerHiredListView",
                );
                return {
                  Component: PartnerHiredListView,
                };
              },
            },
          ],
        },
        {
          path: "partners/:partnerId/preview",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { PreviewPartnerView } = await lazyRetry(
              () => import("~/team/views/PreviewPartnerView/PreviewPartnerView"),
              "PreviewPartnerView",
            );
            return {
              Component: PreviewPartnerView,
            };
          },
        },
        // PARTNER CONTACTS
        {
          path: "partners/:partnerId/contact-list/:contactId",
          errorElement: <EntityErrorHandler renderNotFound={() => <PartnerContactNotFound />} />,
          lazy: async () => {
            const { PartnerContactView } = await lazyRetry(
              () => import("~/team/views/PartnerContactView/PartnerContactView"),
              "PartnerContactView",
            );
            return {
              Component: PartnerContactView,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { PartnerContactProfileView } = await lazyRetry(
                  () => import("~/team/views/PartnerContactProfileView"),
                  "PartnerContactProfileView",
                );
                return {
                  Component: PartnerContactProfileView,
                };
              },
            },
          ],
        },
        {
          path: "partners/:partnerId/contact-list/create",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { CreatePartnerContactView } = await lazyRetry(
              () => import("~/team/views/CreatePartnerContactView"),
              "CreatePartnerContactView",
            );
            return {
              Component: CreatePartnerContactView,
            };
          },
        },
        {
          path: "partners/:partnerId/contact-list/:contactId/edit",
          errorElement: <TopLevelRouteErrorHandler />,
          loader: (args) => {
            return editPartnerContactViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { EditPartnerContact } = await lazyRetry(
              () => import("~/team/views/EditPartnerContactView/EditPartnerContactView"),
              "EditPartnerContact",
            );
            return {
              Component: EditPartnerContact,
            };
          },
        },
        // GAME PLANS
        {
          path: "jobs/game-plan",
          loader: () => {
            return redirect(JOB_PATHS.root);
          },
        },
        {
          path: "jobs/game-plan/:gamePlanId",
          errorElement: <TopLevelRouteErrorHandler />,
          loader: async (args) => {
            return jobGamePlansViewLoader(args, queryClient);
          },
          lazy: async () => {
            const { JobGamePlansView } = await lazyRetry(
              () => import("~/team/views/JobGamePlansView/JobGamePlansView"),
              "JobGamePlansView",
            );
            return {
              Component: JobGamePlansView,
            };
          },
        },
        // CONTENT LIBRARY
        {
          path: "content-library",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { ContentLibraryView } = await lazyRetry(
              () => import("~/team/views/ContentLibraryView"),
              "ContentLibraryView",
            );
            return {
              Component: ContentLibraryView,
            };
          },
        },
        {
          path: "content-library/:contentId",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamContentLibraryNotFound />} />,
          lazy: async () => {
            const { ContentLibraryItemView } = await lazyRetry(
              () => import("~/team/views/ContentLibraryItemView"),
              "ContentLibraryItemView",
            );
            return {
              Component: ContentLibraryItemView,
            };
          },
        },
        {
          path: "content-library/:contentId/edit",
          errorElement: <EntityErrorHandler renderNotFound={() => <TeamContentLibraryNotFound />} />,
          lazy: async () => {
            const { EditContentLibraryItemView } = await lazyRetry(
              () => import("~/team/views/EditContentLibraryItemView"),
              "EditContentLibraryItemView",
            );
            return {
              Component: EditContentLibraryItemView,
            };
          },
        },
        {
          path: "content-library/create",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { CreateContentLibraryItemView } = await lazyRetry(
              () => import("~/team/views/EditContentLibraryItemView"),
              "CreateContentLibraryItemView",
            );
            return {
              Component: CreateContentLibraryItemView,
            };
          },
        },
        {
          path: "hired",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { HiredView } = await lazyRetry(() => import("~/team/views/HiredView"), "HiredView");
            return {
              Component: HiredView,
            };
          },
        },
        {
          path: "automation",
          errorElement: <TopLevelRouteErrorHandler />,
          lazy: async () => {
            const { AutomationView } = await lazyRetry(() => import("~/team/views/AutomationView"), "AutomationView");
            return {
              Component: AutomationView,
            };
          },
          children: [
            {
              index: true,
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { AutomationStageView } = await lazyRetry(
                  () => import("~/team/views/AutomationStageView"),
                  "AutomationStageView",
                );
                return {
                  Component: AutomationStageView,
                };
              },
            },
            {
              path: "stage/resource",
              loader: () => {
                return redirect(AUTOMATION_PATHS.root);
              },
            },
            {
              path: "partner/resource",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { AutomationPartnerView } = await lazyRetry(
                  () => import("~/team/views/AutomationPartnerView"),
                  "AutomationPartnerView",
                );
                return {
                  Component: AutomationPartnerView,
                };
              },
            },
            {
              path: "function/resource",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { AutomationFunctionView } = await lazyRetry(
                  () => import("~/team/views/AutomationFunctionView"),
                  "AutomationFunctionView",
                );
                return {
                  Component: AutomationFunctionView,
                };
              },
            },
            {
              path: "module/resource",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { AutomationModuleView } = await lazyRetry(
                  () => import("~/team/views/AutomationModuleView"),
                  "AutomationModuleView",
                );
                return {
                  Component: AutomationModuleView,
                };
              },
            },
            {
              path: "stage/survey",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { AutomationStageSurveyView } = await lazyRetry(
                  () => import("~/team/views/AutomationStageSurveyView"),
                  "AutomationStageSurveyView",
                );
                return {
                  Component: AutomationStageSurveyView,
                };
              },
            },
            {
              path: "event/survey",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { AutomationEventSurveyView } = await lazyRetry(
                  () => import("~/team/views/AutomationEventSurveyView"),
                  "AutomationEventSurveyView",
                );
                return {
                  Component: AutomationEventSurveyView,
                };
              },
            },
            {
              path: "platform-event/survey",
              errorElement: <NestedRouteErrorHandler />,
              lazy: async () => {
                const { AutomationPlatformEventSurveyView } = await lazyRetry(
                  () => import("~/team/views/AutomationPlatformEventSurveyView"),
                  "AutomationPlatformEventSurveyView",
                );
                return {
                  Component: AutomationPlatformEventSurveyView,
                };
              },
            },
          ],
        },
        {
          path: "*",
          element: (
            <div className="content-container flex min-h-[calc(100dvh-var(--siteheader-height))] items-center justify-center">
              <NotFound />
            </div>
          ),
        },
      ],
    },
  ];
}
